import { useCallback, useState } from "react";
import { FIELD_NAME_MAP } from "./constants";
import { useClickElement } from "../../../../../hooks/useClickElement";

export const validateAndGetImageUrl = (
  image,
  validFormats,
  minSize = { width: 0, height: 0 },
  maxSize = { width: 20000, height: 20000 }
) => {
  const imageData = image?.formats ? image.formats[0] : image;

  if (!imageData?.src) return null;

  const format = (imageData.format || "").toLowerCase();
  const normalizedFormat = format === "jpg" ? "jpeg" : format;
  const allowedFormats = validFormats.map(f =>
    f.toLowerCase() === "jpg" ? "jpeg" : f.toLowerCase()
  );

  const hasNoDimensions = !imageData.width && !imageData.height;
  const isVectorOrWebp = normalizedFormat === "svg" || normalizedFormat === "webp";
  const hasValidDimensions =
    (isVectorOrWebp && hasNoDimensions) ||
    (imageData.width >= minSize.width &&
      imageData.height >= minSize.height &&
      imageData.width <= maxSize.width &&
      imageData.height <= maxSize.height);

  if (allowedFormats.includes(normalizedFormat) && hasValidDimensions) {
    return imageData.src;
  }

  if (isVectorOrWebp && hasNoDimensions && imageData.src) {
    return imageData.src;
  }

  return null;
};

const LOGO_FORMAT_PRIORITY = ["svg", "png", "webp", "jpeg", "jpg"];

const normalizeImageFormat = (format: string | undefined): string => {
  const lower = (format || "").toLowerCase();
  return lower === "jpg" ? "jpeg" : lower;
};

const pickBestLogoFormat = (formats: { src?: string; format?: string }[] = []) => {
  for (const preferredFormat of LOGO_FORMAT_PRIORITY) {
    const match = formats.find(
      format => normalizeImageFormat(format.format) === preferredFormat
    );
    if (match?.src) {
      return match;
    }
  }

  return formats.find(format => format?.src) ?? null;
};

const LOGO_ALLOWED_FORMATS = new Set(["png", "jpeg", "jpg", "svg", "webp"]);

const isAllowedLogoFormat = (format: string | undefined): boolean =>
  LOGO_ALLOWED_FORMATS.has(normalizeImageFormat(format || ""));

export const pickBrandfetchNavbarLogo = (brandFetchResponse): string | null => {
  const logos = Array.isArray(brandFetchResponse)
    ? brandFetchResponse
    : brandFetchResponse?.logos;
  if (!Array.isArray(logos) || logos.length === 0) {
    return null;
  }

  const preferredLogos = [
    ...logos.filter(logo => logo.type === "logo" && logo.theme === "dark"),
    ...logos.filter(logo => logo.type === "logo" && logo.theme === "light"),
    ...logos.filter(logo => logo.type === "logo"),
  ];

  for (const logo of preferredLogos) {
    const format = pickBestLogoFormat(logo.formats);
    if (format?.src && isAllowedLogoFormat(format.format)) {
      return format.src;
    }
  }

  return null;
};

const ICON_FORMAT_PRIORITY = ["png", "jpeg", "jpg", "webp", "svg"];

const pickBestIconFormat = (formats: { src?: string; format?: string }[] = []) => {
  for (const preferredFormat of ICON_FORMAT_PRIORITY) {
    const match = formats.find(
      format => normalizeImageFormat(format.format) === preferredFormat
    );
    if (match?.src) {
      return match;
    }
  }

  return formats.find(format => format?.src) ?? null;
};

export const pickBrandfetchIcon = (brandFetchResponse): string | null => {
  const logos = Array.isArray(brandFetchResponse)
    ? brandFetchResponse
    : brandFetchResponse?.logos;
  if (!Array.isArray(logos) || logos.length === 0) {
    return null;
  }

  const preferredIcons = [
    ...logos.filter(logo => logo.type === "icon"),
    ...logos.filter(logo => logo.type === "symbol"),
  ];

  for (const icon of preferredIcons) {
    const format = pickBestIconFormat(icon.formats);
    if (format?.src && isAllowedLogoFormat(format.format)) {
      return format.src;
    }
  }

  return null;
};

const resolveBrandfetchColorMap = (colors: { hex?: string; type?: string }[] = []) => {
  const byType = colors.reduce((acc, color) => {
    if (color?.hex && color?.type) {
      acc[color.type] = color.hex;
    }
    return acc;
  }, {} as Record<string, string>);

  const accent =
    byType.accent ||
    byType.brand ||
    byType.primary ||
    byType.dark ||
    Object.values(byType)[0];
  const dark = byType.dark || byType.brand || accent;
  const light = byType.light || byType.accent || byType.brand || accent;

  return { accent, dark, light };
};

const setDefaultField = (
  target: Record<string, unknown>,
  key: string,
  value: unknown
) => {
  if (value === null || value === undefined || value === "") {
    return;
  }

  const existing = target[key];
  if (existing === null || existing === undefined || existing === "") {
    target[key] = value;
  }
};

const applyBrandfetchCustomizationDefaults = (
  parsedData: Record<string, unknown>,
  brandFetchResponse: { name?: string; longDescription?: string; description?: string }
) => {
  const organizationName =
    (parsedData.organizationName as string) || brandFetchResponse?.name || "";
  const year = new Date().getFullYear();
  const description =
    brandFetchResponse?.longDescription ||
    brandFetchResponse?.description ||
    "";

  setDefaultField(parsedData, "newsfeedNavbarGiveButtonLabel", "Give Now");
  setDefaultField(parsedData, "newsfeedBodyGiveButtonLabel", "Give Now");
  setDefaultField(
    parsedData,
    "newsfeedFooterFirstPart",
    organizationName ? `${organizationName} ${year}` : `${year}`
  );
  setDefaultField(
    parsedData,
    "newsfeedFooterSecondPart",
    organizationName
      ? `© ${year} ${organizationName}. All rights reserved.`
      : `© ${year}. All rights reserved.`
  );
  setDefaultField(
    parsedData,
    "givingBannerMainMessageDesktop",
    organizationName || "Support Our Mission"
  );
  setDefaultField(
    parsedData,
    "givingBannerMainMessageMobile",
    organizationName || "Support Our Mission"
  );
  setDefaultField(
    parsedData,
    "givingBannerSecondaryMessageDesktop",
    description
  );
  setDefaultField(
    parsedData,
    "givingBannerSecondaryMessageMobile",
    description
  );
  setDefaultField(
    parsedData,
    "givingBodyOneTimeMessageText",
    "Your gift makes a difference."
  );
  setDefaultField(
    parsedData,
    "givingBodyMonthlyMessageText",
    "Your monthly gift sustains our mission."
  );
  setDefaultField(parsedData, "givingBodyAmount1", "25");
  setDefaultField(parsedData, "givingBodyAmount2", "50");
  setDefaultField(parsedData, "givingBodyAmount3", "100");
  setDefaultField(parsedData, "givingBodyAmount4", "250");

  const accent = parsedData.newsfeedNavbarGiveButtonColor as string | undefined;
  setDefaultField(parsedData, "newsfeedBodyGiveButtonColor", accent);
  setDefaultField(parsedData, "newsfeedBodyShareColor", accent);
  setDefaultField(parsedData, "newsfeedNavbarHighlightColor", accent);
  setDefaultField(parsedData, "givingBodyArrowColor", accent);
  setDefaultField(
    parsedData,
    "givingBodySelectedAmountColor",
    parsedData.givingBodyHeadersAndAmountColor
  );
  setDefaultField(
    parsedData,
    "givingBodySelectedFrequencyColor",
    parsedData.givingBodyHeadersAndAmountColor
  );
  setDefaultField(
    parsedData,
    "givingSideParagraphStrongTextColor",
    parsedData.givingSideParagraphTextColor
  );
  setDefaultField(
    parsedData,
    "newsfeedHeaderTitle",
    (parsedData.organizationName as string) || brandFetchResponse?.name || ""
  );
};

const defaultNewsfeedHeaderTitle = (parsedData: Record<string, unknown>) => {
  if (
    !parsedData.newsfeedHeaderTitle &&
    parsedData.organizationName
  ) {
    parsedData.newsfeedHeaderTitle = parsedData.organizationName;
  }
};

const imageFormatFromUrl = (url: string): string => {
  const match = url.split(/[?#]/)[0].match(/\.(jpe?g|png|webp|gif|svg)$/i);
  if (!match) {
    return "jpeg";
  }
  return match[1].toLowerCase() === "jpg" ? "jpeg" : match[1].toLowerCase();
};

const normalizeBrandfetchImage = (image: any) => {
  if (image?.formats?.length) {
    return {
      type: image.type,
      formats: image.formats,
    };
  }

  const metadata = image?.pictureMetadata;
  if (!metadata?.sourceUrl) {
    return null;
  }

  return {
    type: image.type,
    formats: [
      {
        src: metadata.sourceUrl,
        width: metadata.naturalWidth,
        height: metadata.naturalHeight,
        format: imageFormatFromUrl(metadata.sourceUrl),
      },
    ],
  };
};

export const parseBrandfetchData = (selectedAssets, brandFetchResponse) => {
  const parsedData = {} as any;

  if (selectedAssets.useBrandName) {
    parsedData.organizationName = brandFetchResponse.name;
  }

  if (selectedAssets.useColors) {
    const colorMap = resolveBrandfetchColorMap(brandFetchResponse.colors);
    parsedData.newsfeedNavbarGiveButtonColor = colorMap.accent;
    parsedData.newsfeedNavbarHighlightColor = colorMap.accent;
    parsedData.newsfeedBodyGiveButtonColor = colorMap.accent;
    parsedData.newsfeedBodyShareColor = colorMap.accent;
    parsedData.givingBodyHeadersAndAmountColor = colorMap.dark;
    parsedData.givingBodyArrowColor = colorMap.accent;
    parsedData.givingBodyInactiveTabColor = colorMap.light;
    parsedData.givingBannerMainMessageDesktopColor = colorMap.dark;
    parsedData.givingBannerMainMessageMobileColor = colorMap.accent;
    parsedData.givingBannerSecondaryMessageDesktopColor = colorMap.light;
    parsedData.givingBannerSecondaryMessageMobileColor = colorMap.dark;
    parsedData.givingSideParagraphTextColor = colorMap.accent;
  }

  if (selectedAssets.useDescription) {
    parsedData.givingSideParagraphText = brandFetchResponse.longDescription;
  }

  if (selectedAssets.useImages && brandFetchResponse.images?.length) {
    const imagesToCheck = brandFetchResponse.images
      .map(normalizeBrandfetchImage)
      .filter(Boolean);

    if (imagesToCheck.length > 0) {
      const validImages = imagesToCheck
        .map(image =>
          validateAndGetImageUrl(image, ["png", "jpeg", "jpg", "webp"], {
            width: 300,
            height: 200,
          })
        )
        .filter(image => image !== null);

      if (validImages.length === 1) {
        parsedData.givingBannerImageDesktop = validImages[0];
        parsedData.givingBannerImageMobile = validImages[0];
      } else if (validImages.length > 1) {
        parsedData.givingBannerImageDesktop = validImages[0];
        parsedData.givingBannerImageMobile = validImages[1];
      }
    }
  }

  if (selectedAssets.useLogos) {
    const navbarLogo = pickBrandfetchNavbarLogo(brandFetchResponse);
    if (navbarLogo) {
      parsedData.newsfeedNavbarLogo = navbarLogo;
    }
  }

  if (selectedAssets.useFavicon) {
    const siteIcon = pickBrandfetchIcon(brandFetchResponse);
    if (siteIcon) {
      parsedData.siteFaviconIcon = siteIcon;
    }
  }

  applyBrandfetchCustomizationDefaults(parsedData, brandFetchResponse);
  defaultNewsfeedHeaderTitle(parsedData);

  return parsedData;
};

export const createFieldKey = (
  targetSectionId,
  selector,
  attribute,
  styleProperties,
  mediaQuery
) => {
  let key = `${targetSectionId}_${selector}_${attribute}`;

  if (attribute === "style" && styleProperties) {
    const props = Array.isArray(styleProperties)
      ? styleProperties.join(",")
      : styleProperties;
    key += `_${props}`;
  }

  if (mediaQuery) {
    key += `_${mediaQuery}`;
  }

  return key;
};

export const withChangeTracking = (hookFn, trackChanges) => {
  return (...args) => {
    const result = hookFn(...args);

    const [
      ,
      ,
      ,
      targetSectionId,
      selector,
      attribute,
      styleProperties,
      ,
      mediaQuery,
    ] = args;
    const key = createFieldKey(
      targetSectionId,
      selector,
      attribute,
      styleProperties,
      mediaQuery
    );
    const fieldName = FIELD_NAME_MAP[key] || key;

    const trackedOnChange = useCallback(
      value => {
        result.onChange(value);
        trackChanges(fieldName, value);
      },
      [result.onChange, fieldName]
    );

    return {
      ...result,
      onChange: trackedOnChange,
    };
  };
};

export const getVariablesFromIframe = (
  SECTIONS,
  useSectionField,
  iframeRef,
  iframeOrigin,
  selectedSection
) => {
  const {
    value: newsfeedNavbarLogo,
    onChange: setNewsfeedNavbarLogo,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.NEWSFEED_NAVBAR.id,
    ".logo-web",
    "style",
    "background"
  );
  const [organizationName, setOrganizationName] = useState("");
  const [newsfeedHeaderTitle, setNewsfeedHeaderTitle] = useState("");
  const [siteFaviconIcon, setSiteFaviconIcon] = useState<string | null>(null);
  const [newsfeedShowHeaderTitle, setNewsfeedShowHeaderTitle] = useState(false);
  const {
    value: newsfeedNavbarGiveButtonLabel,
    onChange: setNewsfeedNavbarGiveButtonLabel,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.NEWSFEED_NAVBAR.id,
    ".give-now-button-nav",
    "text"
  );
  const {
    value: newsfeedNavbarGiveButtonColor,
    onChange: setNewsfeedNavbarGiveButtonColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.NEWSFEED_NAVBAR.id,
    ".give-now-button-nav",
    "style",
    ["background-color", "border-color"]
  );
  const {
    value: newsfeedNavbarHighlightColor,
    onChange: setNewsfeedNavbarHighlightColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.NEWSFEED_NAVBAR.id,
    ".headerV2",
    "style",
    "border-bottom-color"
  );

  const {
    value: newsfeedNavbarToggleColor,
    onChange: setNewsfeedNavbarToggleColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.NEWSFEED_NAVBAR.id,
    ".navbar-toggle",
    "style",
    "border-color",
    true
  );
  const {
    value: newsfeedNavbarToggleBarColor,
    onChange: setNewsfeedNavbarToggleBarColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.NEWSFEED_NAVBAR.id,
    ".icon-bar",
    "style",
    "background-color",
    true
  );

  const {
    value: newsfeedBodyHighlightedTextColor,
    onChange: setNewsfeedBodyHighlightedTextColorVariable,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.NEWSFEED_BODY.id,
    ".title a",
    "style",
    "color",
    true
  );
  const {
    value: newsfeedBodyGiveButtonLabel,
    onChange: setNewsfeedBodyGiveButtonLabel,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.NEWSFEED_BODY.id,
    ".give-now-button",
    "text",
    undefined,
    true
  );
  const {
    value: newsfeedBodyGiveButtonColor,
    onChange: setNewsfeedBodyGiveButtonColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    SECTIONS.NEWSFEED_BODY.id,
    SECTIONS.NEWSFEED_BODY.id,
    ".give-now-button",
    "style",
    ["background-color", "border-color"],
    true
  );
  const {
    value: newsfeedBodyShareColor,
    onChange: setNewsfeedBodyShareColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    SECTIONS.NEWSFEED_BODY.id,
    SECTIONS.NEWSFEED_BODY.id,
    ".post-list .actions .share span",
    "style",
    "color",
    true
  );

  const {
    value: newsfeedFooterFirstPart,
    onChange: setNewsfeedFooterFirstPart,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.NEWSFEED_FOOTER.id,
    ".footer-first-part",
    "text"
  );
  const {
    value: newsfeedFooterSecondPart,
    onChange: setNewsfeedFooterSecondPart,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.NEWSFEED_FOOTER.id,
    ".footer-second-part",
    "text"
  );
  const {
    value: newsfeedFooterVersion,
    onChange: setNewsfeedFooterVersion,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.NEWSFEED_FOOTER.id,
    ".version",
    "text"
  );

  const [givingBodyShowMonthlyDonationNudge, setGivingBodyShowMonthlyDonationNudge] =
    useState(true);
  const {
    value: givingBodyMonthlyDonationNudgeColor,
    onChange: setGivingBodyMonthlyDonationNudgeColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".monthly-gift",
    "style",
    "background-color",
    true
  );
  const {
    value: givingBodyHeadersAndAmountColor,
    onChange: setGivingBodyHeadersAndAmountColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".headers",
    "style",
    "background-color",
    true
  );
  const {
    value: givingBodySelectedAmountColor,
    onChange: setGivingBodySelectedAmountColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".active.selected-amount",
    "style",
    ["background-color", "border-color"],
    true
  );
  const {
    value: givingBodySelectedFrequencyColor,
    onChange: setGivingBodySelectedFrequencyColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".underLine",
    "style",
    "border-color",
    true
  );
  const {
    value: givingBodyArrowColor,
    onChange: setGivingBodyArrowColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".arrow-up.arrowActive",
    "style",
    "color",
    true
  );
  const {
    value: givingBodyInactiveTabColor,
    onChange: setGivingBodyInactiveTabColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".inactive",
    "style",
    "background-color",
    true
  );
  const {
    value: givingBodyOneTimeMessageText,
    onChange: setGivingBodyOneTimeMessageText,
    fetchInitialValue: fetchInitialGivingBodyOneTimeMessageText,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".text.one-time",
    "text"
  );
  const {
    value: givingBodyMonthlyMessageText,
    onChange: setGivingBodyMonthlyMessageText,
    fetchInitialValue: fetchInitialGivingBodyMonthlyMessageText,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".text.monthly",
    "text"
  );

  const {
    value: givingBodyAmount1,
    onChange: setGivingBodyAmount1,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".amounts .btnCol:nth-child(1) button",
    "text"
  );
  const {
    value: givingBodyAmount2,
    onChange: setGivingBodyAmount2,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".amounts .btnCol:nth-child(2) button",
    "text"
  );
  const {
    value: givingBodyAmount3,
    onChange: setGivingBodyAmount3,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".amounts .btnCol:nth-child(3) button",
    "text"
  );
  const {
    value: givingBodyAmount4,
    onChange: setGivingBodyAmount4,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    ".amounts .btnCol:nth-child(4) button",
    "text"
  );

  const {
    value: givingBannerImageDesktop,
    onChange: setGivingBannerImageDesktop,
    fetchInitialValue: fetchGivingBannerImage,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".topBanner",
    "style",
    "background-image"
  );
  const {
    value: givingBannerImageMobile,
    onChange: setGivingBannerImageMobile,
    fetchInitialValue: fetchGivingBannerImageMobile,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".topBanner",
    "style",
    "background-image",
    true,
    "(max-width: 992px)",
    true
  );
  const {
    value: givingBannerOverlayColor,
    onChange: setGivingBannerOverlayColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".topBanner",
    "style",
    "color"
  );

  const {
    value: givingBannerOverlayDisplay,
    onChange: setGivingBannerOverlayDisplay,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".topBanner",
    "style",
    "box-shadow"
  );

  const [givingBannerIncludeOverlay, setGivingBannerIncludeOverlay] = useState(
    givingBannerOverlayDisplay !== "inset 0 0 0 10000px"
  );

  const {
    value: givingBannerMainMessageDesktop,
    onChange: setGivingBannerMainMessageDesktop,
    fetchInitialValue: fetchGivingBannerMainMessageDesktop,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".first",
    "text"
  );

  const {
    value: givingBannerMainMessageMobile,
    onChange: setGivingBannerMainMessageMobile,
    fetchInitialValue: fetchGivingBannerMainMessageMobile,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".middleBanner .first",
    "text"
  );

  const {
    value: givingBannerMainMessageDesktopColor,
    onChange: setGivingBannerMainMessageDesktopColor,
    fetchInitialValue: fetchGivingBannerMainMessageDesktopColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".first",
    "style",
    "color"
  );
  const {
    value: givingBannerMainMessageMobileColor,
    onChange: setGivingBannerMainMessageMobileColor,
    fetchInitialValue: fetchGivingBannerMainMessageMobileColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".middleBanner .first",
    "style",
    "color"
  );
  const {
    value: givingBannerSecondaryMessageDesktop,
    onChange: setGivingBannerSecondaryMessageDesktop,
    fetchInitialValue: fetchGivingBannerSecondaryMessageDesktop,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".topBanner .second",
    "text"
  );
  const {
    value: givingBannerSecondaryMessageMobile,
    onChange: setGivingBannerSecondaryMessageMobile,
    fetchInitialValue: fetchGivingBannerSecondaryMessageMobile,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".middleBanner .second",
    "text"
  );
  const {
    value: givingBannerSecondaryMessageDesktopColor,
    onChange: setGivingBannerSecondaryMessageDesktopColor,
    fetchInitialValue: fetchGivingBannerSecondaryMessageDesktopColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".topBanner .second",
    "style",
    "color"
  );
  const {
    value: givingBannerSecondaryMessageMobileColor,
    onChange: setGivingBannerSecondaryMessageMobileColor,
    fetchInitialValue: fetchGivingBannerSecondaryMessageMobileColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".middleBanner .second",
    "style",
    "color"
  );
  const {
    value: givingBannerMainMessageAlignment,
    onChange: setGivingBannerMainMessageAlignment,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".first",
    "style",
    "text-align"
  );

  const {
    value: givingBannerMainMessageTransform,
    onChange: setGivingBannerMainMessageTransform,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".first",
    "style",
    "text-transform"
  );

  const {
    value: givingBannerSecondaryMessageAlignment,
    onChange: setGivingBannerSecondaryMessageAlignment,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BANNER.id,
    ".topBanner .second",
    "style",
    "text-align"
  );

  const [
    givingBannerMainMessageActiveButtons,
    setGivingBannerMainMessageActiveButtons,
  ] = useState({
    textFormat: givingBannerMainMessageTransform,
    textAlignment: givingBannerMainMessageAlignment,
  });

  const [
    givingBannerSecondaryMessageActiveButtons,
    setGivingBannerSecondaryMessageActiveButtons,
  ] = useState({
    textAlignment: givingBannerSecondaryMessageAlignment,
  });

  const { clickElement: clickOneTime } = useClickElement(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    "one-time"
  );

  const { clickElement: clickMonthly } = useClickElement(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_BODY.id,
    "monthly"
  );

  const {
    value: givingSideParagraphText,
    onChange: setGivingSideParagraphText,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_SIDE_PARAGRAPH.id,
    ".second.side-paragraph",
    "html"
  );
  const {
    value: givingSideParagraphTextColor,
    onChange: setGivingSideParagraphTextColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_SIDE_PARAGRAPH.id,
    ".second",
    "style",
    "color"
  );
  const {
    value: givingSideParagraphStrongTextColor,
    onChange: setGivingSideParagraphStrongTextColor,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.GIVING_SIDE_PARAGRAPH.id,
    ".rightText .second strong",
    "style",
    "color",
    true
  );

  const {
    value: postDetailGiveButtonLabel,
    onChange: setPostDetailGiveButtonLabel,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.POST_DETAIL_MAIN.id,
    ".give-now-button-details",
    "text"
  );
  const [postNormalCase, setPostNormalCase] = useState(false);
  const {
    value: postDetailBioDescription,
    onChange: setPostDetailBioDescription,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.POST_DETAIL_BIO.id,
    ".prime-paragraph-description",
    "html"
  );
  const {
    value: postDetailBioImage,
    onChange: setPostDetailBioImage,
    fetchInitialValue: fetchPostDetailBioImage,
  } = useSectionField(
    iframeRef,
    iframeOrigin,
    selectedSection.id,
    SECTIONS.POST_DETAIL_BIO.id,
    ".prime-image-description",
    "style",
    "background-image"
  );

  return {
    newsfeedNavbarLogo,
    setNewsfeedNavbarLogo,
    organizationName,
    setOrganizationName,
    newsfeedHeaderTitle,
    setNewsfeedHeaderTitle,
    siteFaviconIcon,
    setSiteFaviconIcon,
    newsfeedShowHeaderTitle,
    setNewsfeedShowHeaderTitle,
    newsfeedNavbarGiveButtonLabel,
    setNewsfeedNavbarGiveButtonLabel,
    newsfeedNavbarGiveButtonColor,
    setNewsfeedNavbarGiveButtonColor,
    newsfeedNavbarHighlightColor,
    setNewsfeedNavbarHighlightColor,
    newsfeedNavbarToggleColor,
    setNewsfeedNavbarToggleColor,
    newsfeedNavbarToggleBarColor,
    setNewsfeedNavbarToggleBarColor,
    newsfeedBodyHighlightedTextColor,
    setNewsfeedBodyHighlightedTextColorVariable,
    newsfeedBodyGiveButtonLabel,
    setNewsfeedBodyGiveButtonLabel,
    newsfeedBodyGiveButtonColor,
    setNewsfeedBodyGiveButtonColor,
    newsfeedBodyShareColor,
    setNewsfeedBodyShareColor,
    newsfeedFooterFirstPart,
    setNewsfeedFooterFirstPart,
    newsfeedFooterSecondPart,
    setNewsfeedFooterSecondPart,
    newsfeedFooterVersion,
    setNewsfeedFooterVersion,
    givingBodyShowMonthlyDonationNudge,
    setGivingBodyShowMonthlyDonationNudge,
    givingBodyMonthlyDonationNudgeColor,
    setGivingBodyMonthlyDonationNudgeColor,
    givingBodyHeadersAndAmountColor,
    setGivingBodyHeadersAndAmountColor,
    givingBodySelectedAmountColor,
    setGivingBodySelectedAmountColor,
    givingBodySelectedFrequencyColor,
    setGivingBodySelectedFrequencyColor,
    givingBodyArrowColor,
    setGivingBodyArrowColor,
    givingBodyInactiveTabColor,
    setGivingBodyInactiveTabColor,
    givingBodyOneTimeMessageText,
    setGivingBodyOneTimeMessageText,
    fetchInitialGivingBodyOneTimeMessageText,
    givingBodyMonthlyMessageText,
    setGivingBodyMonthlyMessageText,
    fetchInitialGivingBodyMonthlyMessageText,
    givingBodyAmount1,
    setGivingBodyAmount1,
    givingBodyAmount2,
    setGivingBodyAmount2,
    givingBodyAmount3,
    setGivingBodyAmount3,
    givingBodyAmount4,
    setGivingBodyAmount4,
    givingBannerImageDesktop,
    setGivingBannerImageDesktop,
    fetchGivingBannerImage,
    givingBannerImageMobile,
    setGivingBannerImageMobile,
    fetchGivingBannerImageMobile,
    givingBannerOverlayColor,
    setGivingBannerOverlayColor,
    givingBannerOverlayDisplay,
    setGivingBannerOverlayDisplay,
    givingBannerIncludeOverlay,
    setGivingBannerIncludeOverlay,
    givingBannerMainMessageDesktop,
    setGivingBannerMainMessageDesktop,
    fetchGivingBannerMainMessageDesktop,
    givingBannerMainMessageMobile,
    setGivingBannerMainMessageMobile,
    fetchGivingBannerMainMessageMobile,
    givingBannerMainMessageDesktopColor,
    setGivingBannerMainMessageDesktopColor,
    fetchGivingBannerMainMessageDesktopColor,
    givingBannerMainMessageMobileColor,
    setGivingBannerMainMessageMobileColor,
    fetchGivingBannerMainMessageMobileColor,
    givingBannerSecondaryMessageDesktop,
    setGivingBannerSecondaryMessageDesktop,
    fetchGivingBannerSecondaryMessageDesktop,
    givingBannerSecondaryMessageMobile,
    setGivingBannerSecondaryMessageMobile,
    fetchGivingBannerSecondaryMessageMobile,
    givingBannerSecondaryMessageDesktopColor,
    setGivingBannerSecondaryMessageDesktopColor,
    fetchGivingBannerSecondaryMessageDesktopColor,
    givingBannerSecondaryMessageMobileColor,
    setGivingBannerSecondaryMessageMobileColor,
    fetchGivingBannerSecondaryMessageMobileColor,
    givingBannerMainMessageAlignment,
    setGivingBannerMainMessageAlignment,
    givingBannerMainMessageTransform,
    setGivingBannerMainMessageTransform,
    givingBannerSecondaryMessageAlignment,
    setGivingBannerSecondaryMessageAlignment,
    givingBannerMainMessageActiveButtons,
    setGivingBannerMainMessageActiveButtons,
    givingBannerSecondaryMessageActiveButtons,
    setGivingBannerSecondaryMessageActiveButtons,
    clickOneTime,
    clickMonthly,
    givingSideParagraphText,
    setGivingSideParagraphText,
    givingSideParagraphTextColor,
    setGivingSideParagraphTextColor,
    givingSideParagraphStrongTextColor,
    setGivingSideParagraphStrongTextColor,
    postDetailGiveButtonLabel,
    setPostDetailGiveButtonLabel,
    postNormalCase,
    setPostNormalCase,
    postDetailBioDescription,
    setPostDetailBioDescription,
    postDetailBioImage,
    setPostDetailBioImage,
    fetchPostDetailBioImage,
  };
};

export interface CustomizationEditorState {
  organizationName: string;
  newsfeedHeaderTitle: string;
  newsfeedShowHeaderTitle: boolean;
  newsfeedNavbarLogo: string;
  siteFaviconIcon: string | null;
  newsfeedNavbarGiveButtonLabel: string;
  newsfeedNavbarGiveButtonColor: string;
  newsfeedNavbarHighlightColor: string;
  newsfeedBodyGiveButtonLabel: string;
  newsfeedBodyGiveButtonColor: string;
  newsfeedBodyShareColor: string;
  newsfeedFooterFirstPart: string;
  newsfeedFooterSecondPart: string;
  givingBannerImageDesktop: string | null;
  givingBannerImageMobile: string | null;
  givingBannerOverlayDisplay: string;
  givingBannerMainMessageDesktop: string;
  givingBannerMainMessageMobile: string;
  givingBannerMainMessageDesktopColor: string;
  givingBannerMainMessageMobileColor: string;
  givingBannerSecondaryMessageDesktop: string;
  givingBannerSecondaryMessageMobile: string;
  givingBannerSecondaryMessageDesktopColor: string;
  givingBannerSecondaryMessageMobileColor: string;
  givingBannerMainMessageActiveButtons: { textAlignment?: string; textFormat?: string };
  givingBannerSecondaryMessageActiveButtons: { textAlignment?: string };
  givingBodyHeadersAndAmountColor: string;
  givingBodyInactiveTabColor: string;
  givingBodyShowMonthlyDonationNudge: boolean;
  givingBodyMonthlyDonationNudgeColor: string;
  givingBodyOneTimeMessageText: string;
  givingBodyMonthlyMessageText: string;
  givingBodyAmount1: string;
  givingBodyAmount2: string;
  givingBodyAmount3: string;
  givingBodyAmount4: string;
  givingSideParagraphText: string;
  givingSideParagraphTextColor: string;
  giftDesignationProjects: Array<{ id?: string | null; name: string }>;
  postDetailLayout: string;
  postDetailGiveButtonLabel: string;
  postDetailBioDescription: string;
  postDetailBioImage: string | null;
  postNormalCase: boolean;
}

const putStringField = (
  target: Record<string, unknown>,
  key: string,
  value: unknown
) => {
  if (value !== null && value !== undefined && value !== "") {
    target[key] = value;
  }
};

/**
 * Builds the RTDB / deploy payload from live editor state.
 * Image uploads and iframe-driven fields all flow through here so auto-save and publish stay in sync.
 */
export const buildCustomizationSnapshot = (
  state: CustomizationEditorState
): Record<string, unknown> => {
  const snapshot: Record<string, unknown> = {};

  putStringField(snapshot, "organizationName", state.organizationName);

  const headerTitle = state.newsfeedHeaderTitle || state.organizationName;
  putStringField(snapshot, "newsfeedHeaderTitle", headerTitle);

  if (state.newsfeedShowHeaderTitle !== undefined && state.newsfeedShowHeaderTitle !== null) {
    snapshot.newsfeedShowHeaderTitle = state.newsfeedShowHeaderTitle;
  }

  putStringField(snapshot, "newsfeedNavbarLogo", state.newsfeedNavbarLogo);
  putStringField(snapshot, "siteFaviconIcon", state.siteFaviconIcon);
  putStringField(snapshot, "newsfeedNavbarGiveButtonLabel", state.newsfeedNavbarGiveButtonLabel);
  putStringField(snapshot, "newsfeedNavbarGiveButtonColor", state.newsfeedNavbarGiveButtonColor);
  putStringField(snapshot, "newsfeedNavbarHighlightColor", state.newsfeedNavbarHighlightColor);
  putStringField(snapshot, "newsfeedNavbarToggleColor", state.newsfeedNavbarHighlightColor);
  putStringField(snapshot, "newsfeedNavbarToggleBarColor", state.newsfeedNavbarHighlightColor);
  putStringField(snapshot, "newsfeedBodyGiveButtonLabel", state.newsfeedBodyGiveButtonLabel);
  putStringField(snapshot, "newsfeedBodyGiveButtonColor", state.newsfeedBodyGiveButtonColor);
  putStringField(snapshot, "newsfeedBodyShareColor", state.newsfeedBodyShareColor);
  putStringField(snapshot, "newsfeedFooterFirstPart", state.newsfeedFooterFirstPart);
  putStringField(snapshot, "newsfeedFooterSecondPart", state.newsfeedFooterSecondPart);

  putStringField(snapshot, "givingBannerImageDesktop", state.givingBannerImageDesktop);
  putStringField(snapshot, "givingBannerImageMobile", state.givingBannerImageMobile);
  putStringField(snapshot, "givingBannerOverlayDisplay", state.givingBannerOverlayDisplay);
  putStringField(snapshot, "givingBannerMainMessageDesktop", state.givingBannerMainMessageDesktop);
  putStringField(snapshot, "givingBannerMainMessageMobile", state.givingBannerMainMessageMobile);
  putStringField(
    snapshot,
    "givingBannerMainMessageDesktopColor",
    state.givingBannerMainMessageDesktopColor
  );
  putStringField(
    snapshot,
    "givingBannerMainMessageMobileColor",
    state.givingBannerMainMessageMobileColor
  );
  putStringField(
    snapshot,
    "givingBannerSecondaryMessageDesktop",
    state.givingBannerSecondaryMessageDesktop
  );
  putStringField(
    snapshot,
    "givingBannerSecondaryMessageMobile",
    state.givingBannerSecondaryMessageMobile
  );
  putStringField(
    snapshot,
    "givingBannerSecondaryMessageDesktopColor",
    state.givingBannerSecondaryMessageDesktopColor
  );
  putStringField(
    snapshot,
    "givingBannerSecondaryMessageMobileColor",
    state.givingBannerSecondaryMessageMobileColor
  );

  putStringField(
    snapshot,
    "givingBannerMainMessageAlignment",
    state.givingBannerMainMessageActiveButtons?.textAlignment
  );
  putStringField(
    snapshot,
    "givingBannerMainMessageTransform",
    state.givingBannerMainMessageActiveButtons?.textFormat
  );
  putStringField(
    snapshot,
    "givingBannerSecondaryMessageAlignment",
    state.givingBannerSecondaryMessageActiveButtons?.textAlignment
  );

  putStringField(snapshot, "givingBodyHeadersAndAmountColor", state.givingBodyHeadersAndAmountColor);
  putStringField(snapshot, "givingBodySelectedAmountColor", state.givingBodyHeadersAndAmountColor);
  putStringField(snapshot, "givingBodySelectedFrequencyColor", state.givingBodyHeadersAndAmountColor);
  putStringField(snapshot, "givingBodyArrowColor", state.givingBodyHeadersAndAmountColor);
  putStringField(snapshot, "givingBodyInactiveTabColor", state.givingBodyInactiveTabColor);
  if (
    state.givingBodyShowMonthlyDonationNudge !== undefined &&
    state.givingBodyShowMonthlyDonationNudge !== null
  ) {
    snapshot.givingBodyShowMonthlyDonationNudge = state.givingBodyShowMonthlyDonationNudge;
  }
  putStringField(
    snapshot,
    "givingBodyMonthlyDonationNudgeColor",
    state.givingBodyMonthlyDonationNudgeColor
  );
  putStringField(snapshot, "givingBodyOneTimeMessageText", state.givingBodyOneTimeMessageText);
  putStringField(snapshot, "givingBodyMonthlyMessageText", state.givingBodyMonthlyMessageText);
  putStringField(snapshot, "givingBodyAmount1", state.givingBodyAmount1);
  putStringField(snapshot, "givingBodyAmount2", state.givingBodyAmount2);
  putStringField(snapshot, "givingBodyAmount3", state.givingBodyAmount3);
  putStringField(snapshot, "givingBodyAmount4", state.givingBodyAmount4);

  putStringField(snapshot, "givingSideParagraphText", state.givingSideParagraphText);
  putStringField(snapshot, "givingSideParagraphTextColor", state.givingSideParagraphTextColor);
  putStringField(snapshot, "givingSideParagraphStrongTextColor", state.givingSideParagraphTextColor);

  if (state.giftDesignationProjects?.length) {
    snapshot.giftDesignationProjects = state.giftDesignationProjects.map(project => ({
      name: project.name,
    }));
  }

  putStringField(snapshot, "postDetailLayout", state.postDetailLayout);
  putStringField(snapshot, "postDetailGiveButtonLabel", state.postDetailGiveButtonLabel);
  putStringField(snapshot, "postDetailBioDescription", state.postDetailBioDescription);
  putStringField(snapshot, "postDetailBioImage", state.postDetailBioImage);
  if (state.postNormalCase !== undefined && state.postNormalCase !== null) {
    snapshot.postNormalCase = state.postNormalCase;
  }

  return snapshot;
};

export function pickCustomizationInitValue(
  data: Record<string, unknown>,
  iframeValues: Record<string, unknown> | undefined,
  key: string
): string | undefined {
  if (data[key] !== null && data[key] !== undefined) {
    return String(data[key]);
  }
  const fromIframe = iframeValues?.[key];
  if (fromIframe !== null && fromIframe !== undefined) {
    return String(fromIframe);
  }
  return undefined;
}

export interface GivingBannerInitSetters {
  setGivingBannerImageDesktop: (value: string) => void;
  setGivingBannerImageMobile: (value: string) => void;
  setGivingBannerOverlayColor: (value: string) => void;
  setGivingBannerOverlayDisplay: (value: string) => void;
  setGivingBannerIncludeOverlay: (value: boolean) => void;
  setGivingBannerMainMessageDesktop: (value: string) => void;
  setGivingBannerMainMessageMobile: (value: string) => void;
  setGivingBannerMainMessageDesktopColor: (value: string) => void;
  setGivingBannerMainMessageMobileColor: (value: string) => void;
  setGivingBannerSecondaryMessageDesktop: (value: string) => void;
  setGivingBannerSecondaryMessageMobile: (value: string) => void;
  setGivingBannerSecondaryMessageDesktopColor: (value: string) => void;
  setGivingBannerSecondaryMessageMobileColor: (value: string) => void;
  setGivingBannerMainMessageActiveButtons: (
    value:
      | { textAlignment?: string; textFormat?: string }
      | ((
          prev: { textAlignment?: string; textFormat?: string }
        ) => { textAlignment?: string; textFormat?: string })
  ) => void;
  setGivingBannerSecondaryMessageActiveButtons: (
    value:
      | { textAlignment?: string }
      | ((prev: { textAlignment?: string }) => { textAlignment?: string })
  ) => void;
}

/** Seeds giving-banner editor state from RTDB / iframe init payload. */
export function applyGivingBannerInitValues(
  data: Record<string, unknown>,
  iframeValues: Record<string, unknown> | undefined,
  setters: GivingBannerInitSetters
): void {
  const pick = (key: string) =>
    pickCustomizationInitValue(data, iframeValues, key);

  const imageDesktop = pick("givingBannerImageDesktop");
  if (imageDesktop !== undefined) {
    setters.setGivingBannerImageDesktop(imageDesktop);
  }

  const imageMobile = pick("givingBannerImageMobile");
  if (imageMobile !== undefined) {
    setters.setGivingBannerImageMobile(imageMobile);
  } else if (imageDesktop !== undefined) {
    setters.setGivingBannerImageMobile(imageDesktop);
  }

  const overlayColor = pick("givingBannerOverlayColor");
  if (overlayColor !== undefined) {
    setters.setGivingBannerOverlayColor(overlayColor);
  }

  const overlayDisplay = pick("givingBannerOverlayDisplay");
  if (overlayDisplay !== undefined) {
    setters.setGivingBannerOverlayDisplay(overlayDisplay);
    setters.setGivingBannerIncludeOverlay(
      overlayDisplay !== "inset 0 0 0 10000px" && overlayDisplay !== "none"
    );
  }

  const stringFields: Array<[string, (value: string) => void]> = [
    ["givingBannerMainMessageDesktop", setters.setGivingBannerMainMessageDesktop],
    ["givingBannerMainMessageMobile", setters.setGivingBannerMainMessageMobile],
    [
      "givingBannerMainMessageDesktopColor",
      setters.setGivingBannerMainMessageDesktopColor,
    ],
    [
      "givingBannerMainMessageMobileColor",
      setters.setGivingBannerMainMessageMobileColor,
    ],
    [
      "givingBannerSecondaryMessageDesktop",
      setters.setGivingBannerSecondaryMessageDesktop,
    ],
    [
      "givingBannerSecondaryMessageMobile",
      setters.setGivingBannerSecondaryMessageMobile,
    ],
    [
      "givingBannerSecondaryMessageDesktopColor",
      setters.setGivingBannerSecondaryMessageDesktopColor,
    ],
    [
      "givingBannerSecondaryMessageMobileColor",
      setters.setGivingBannerSecondaryMessageMobileColor,
    ],
  ];

  stringFields.forEach(([key, setter]) => {
    const value = pick(key);
    if (value !== undefined) {
      setter(value);
    }
  });

  const mainAlignment = pick("givingBannerMainMessageAlignment");
  const mainTransform = pick("givingBannerMainMessageTransform");
  if (mainAlignment !== undefined || mainTransform !== undefined) {
    setters.setGivingBannerMainMessageActiveButtons(prev => ({
      ...prev,
      ...(mainAlignment !== undefined ? { textAlignment: mainAlignment } : {}),
      ...(mainTransform !== undefined ? { textFormat: mainTransform } : {}),
    }));
  }

  const secondaryAlignment = pick("givingBannerSecondaryMessageAlignment");
  if (secondaryAlignment !== undefined) {
    setters.setGivingBannerSecondaryMessageActiveButtons(prev => ({
      ...prev,
      textAlignment: secondaryAlignment,
    }));
  }
}

export interface GivingBodyInitSetters {
  setGivingBodyOneTimeMessageText: (value: string) => void;
  setGivingBodyMonthlyMessageText: (value: string) => void;
  setGivingBodyHeadersAndAmountColor: (value: string) => void;
  setGivingBodyInactiveTabColor: (value: string) => void;
  setGivingBodyShowMonthlyDonationNudge: (value: boolean) => void;
  setGivingBodyMonthlyDonationNudgeColor: (value: string) => void;
  setGivingBodyAmount1: (value: string) => void;
  setGivingBodyAmount2: (value: string) => void;
  setGivingBodyAmount3: (value: string) => void;
  setGivingBodyAmount4: (value: string) => void;
}

export function applyGivingBodyInitValues(
  data: Record<string, unknown>,
  iframeValues: Record<string, unknown> | undefined,
  setters: GivingBodyInitSetters
): void {
  const pick = (key: string) =>
    pickCustomizationInitValue(data, iframeValues, key);

  const stringFields: Array<[string, (value: string) => void]> = [
    ["givingBodyOneTimeMessageText", setters.setGivingBodyOneTimeMessageText],
    ["givingBodyMonthlyMessageText", setters.setGivingBodyMonthlyMessageText],
    ["givingBodyHeadersAndAmountColor", setters.setGivingBodyHeadersAndAmountColor],
    ["givingBodyInactiveTabColor", setters.setGivingBodyInactiveTabColor],
    ["givingBodyAmount1", setters.setGivingBodyAmount1],
    ["givingBodyAmount2", setters.setGivingBodyAmount2],
    ["givingBodyAmount3", setters.setGivingBodyAmount3],
    ["givingBodyAmount4", setters.setGivingBodyAmount4],
  ];

  stringFields.forEach(([key, setter]) => {
    const value = pick(key);
    if (value !== undefined) {
      setter(value);
    }
  });

  const showNudge =
    data.givingBodyShowMonthlyDonationNudge ??
    iframeValues?.givingBodyShowMonthlyDonationNudge;
  if (showNudge !== undefined && showNudge !== null) {
    setters.setGivingBodyShowMonthlyDonationNudge(
      showNudge === true || showNudge === "true"
    );
  }

  const nudgeColor = pick("givingBodyMonthlyDonationNudgeColor");
  if (nudgeColor !== undefined) {
    setters.setGivingBodyMonthlyDonationNudgeColor(nudgeColor);
  }
}

export function applyPostDetailInitValues(
  data: Record<string, unknown>,
  iframeValues: Record<string, unknown> | undefined,
  setters: {
    setPostDetailGiveButtonLabel: (value: string) => void;
    setPostNormalCase: (value: boolean) => void;
    setPostDetailBioDescription: (value: string) => void;
    setPostDetailBioImage: (value: string) => void;
    setPostDetailLayout: (value: string) => void;
  }
): void {
  const pick = (key: string) =>
    pickCustomizationInitValue(data, iframeValues, key);

  const giveButtonLabel = pick("postDetailGiveButtonLabel");
  if (giveButtonLabel !== undefined) {
    setters.setPostDetailGiveButtonLabel(giveButtonLabel);
  }

  const bioDescription = pick("postDetailBioDescription");
  if (bioDescription !== undefined) {
    setters.setPostDetailBioDescription(bioDescription);
  }

  const bioImage = pick("postDetailBioImage");
  if (bioImage !== undefined) {
    setters.setPostDetailBioImage(bioImage);
  }

  const layout = pick("postDetailLayout");
  if (layout !== undefined) {
    setters.setPostDetailLayout(layout);
  }

  const normalCase = data.postNormalCase ?? iframeValues?.postNormalCase;
  if (normalCase !== undefined && normalCase !== null) {
    setters.setPostNormalCase(normalCase === true || normalCase === "true");
  }
}

export const buildCustomizationFromBrandSetup = (brandSetup): Record<string, unknown> => {
  if (!brandSetup) {
    return {};
  }

  if (
    brandSetup.setupMethod === "manual" ||
    !brandSetup.selectedAssets ||
    Object.values(brandSetup.selectedAssets).every(value => value === false)
  ) {
    return {};
  }

  const selectedAssets = Object.entries(brandSetup.selectedAssets).reduce(
    (acc, [key, value]) => {
      if (value === true) acc[key] = value;
      return acc;
    },
    {}
  );

  return parseBrandfetchData(selectedAssets, brandSetup.brandfetchResponse);
};

